import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import {
    cellOps,
    IRowRenderType,
    rowOps,
    sectionOps,
    trackOps,
} from "../../../domain";
import type {
    IReactStateFile,
    ISaveStatus,
} from "../../../pages/File/Editor/types";

/**
 * Redux state for open notation documents.
 *
 * This replaces the static `EditorFile.Instances` registry. That registry held
 * live user documents in module scope, which on a Node server would be shared
 * across concurrent requests — two users opening the same file id would get one
 * another's document, with read-only computed from whoever arrived first. Redux
 * state is per-store, and the store becomes per-request in a later step.
 *
 * Reducers call the pure operations from `src/domain`. `current()` unwraps the
 * immer draft first so the ops receive (and return) plain objects.
 */

export interface EditorFileEntry {
    document: IReactStateFile;
    saveStatus: ISaveStatus;
    /** Matra count, derived once from the taal. */
    taalCount: number;
    /**
     * Monotonic counter used ONLY to name new sections ("New Section 4").
     *
     * It deliberately never decreases: reusing a number after a delete would
     * hand two sections the same default name. Bounds checks do NOT use this —
     * they derive from `sections.length` — which is what fixes the corruption
     * where a stale counter let moveSectionDown swap in an undefined section.
     */
    nextSectionNumber: number;
    readOnly: boolean;
    /**
     * True while a write is queued for this document.
     *
     * Lives in state rather than a module-level timer map so the "at most one
     * queued save per file" rule is per-file correct, survives on a server, and
     * needs no bookkeeping outside Redux.
     */
    savePending: boolean;
}

export interface NotationState {
    files: Record<string, EditorFileEntry>;
}

const initialState: NotationState = { files: {} };

/** Narrows a payload to the entry it targets, or undefined if not loaded. */
type WithFile<T = {}> = { fileId: string } & T;

const notationSlice = createSlice({
    name: "notation",
    initialState,
    reducers: {
        fileLoaded: (
            state,
            action: PayloadAction<
                WithFile<{
                    document: IReactStateFile;
                    taalCount: number;
                    readOnly: boolean;
                    saveStatus: ISaveStatus;
                }>
            >
        ) => {
            const { fileId, document, taalCount, readOnly, saveStatus } =
                action.payload;
            state.files[fileId] = {
                document,
                saveStatus,
                taalCount,
                nextSectionNumber: document.sections.length,
                readOnly,
                savePending: false,
            };
        },

        fileClosed: (state, action: PayloadAction<WithFile>) => {
            delete state.files[action.payload.fileId];
        },

        savePendingChanged: (
            state,
            action: PayloadAction<WithFile<{ pending: boolean }>>
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.savePending = action.payload.pending;
        },

        saveStatusChanged: (
            state,
            action: PayloadAction<WithFile<{ saveStatus: ISaveStatus }>>
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.saveStatus = action.payload.saveStatus;
        },

        // --- section operations -------------------------------------------
        addSection: (
            state,
            action: PayloadAction<WithFile<{ position?: number }>>
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            const doc = current(entry.document);
            entry.document = sectionOps.addSection(doc, {
                position: action.payload.position,
                taalCount: entry.taalCount,
                vectorLength: doc.fileOptions.rows.vectorLength,
                nextSectionNumber: entry.nextSectionNumber,
            });
            entry.nextSectionNumber++;
        },

        deleteSection: (
            state,
            action: PayloadAction<WithFile<{ sectionIndex: number }>>
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = sectionOps.deleteSection(
                current(entry.document),
                { sectionIndex: action.payload.sectionIndex }
            );
            // nextSectionNumber is intentionally NOT decremented — see its doc
            // comment. Bounds checks no longer depend on it, so this is safe.
        },

        moveSectionUp: (
            state,
            action: PayloadAction<WithFile<{ sectionIndex: number }>>
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = sectionOps.moveSectionUp(current(entry.document), {
                sectionIndex: action.payload.sectionIndex,
            });
        },

        moveSectionDown: (
            state,
            action: PayloadAction<WithFile<{ sectionIndex: number }>>
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = sectionOps.moveSectionDown(
                current(entry.document),
                { sectionIndex: action.payload.sectionIndex }
            );
        },

        setSectionName: (
            state,
            action: PayloadAction<WithFile<{ sectionIndex: number; name: string }>>
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = sectionOps.setSectionName(current(entry.document), {
                sectionIndex: action.payload.sectionIndex,
                name: action.payload.name,
            });
        },

        // --- row operations -----------------------------------------------
        addRow: (
            state,
            action: PayloadAction<
                WithFile<{ sectionIndex: number; position?: number }>
            >
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            const doc = current(entry.document);
            entry.document = rowOps.addRow(doc, {
                sectionIndex: action.payload.sectionIndex,
                position: action.payload.position,
                taalCount: entry.taalCount,
                vectorLength: doc.fileOptions.rows.vectorLength,
            });
        },

        deleteRow: (
            state,
            action: PayloadAction<WithFile<{ sectionIndex: number; rowIndex: number }>>
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = rowOps.deleteRow(current(entry.document), {
                sectionIndex: action.payload.sectionIndex,
                rowIndex: action.payload.rowIndex,
            });
        },

        moveRowUp: (
            state,
            action: PayloadAction<WithFile<{ sectionIndex: number; rowIndex: number }>>
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = rowOps.moveRowUp(current(entry.document), {
                sectionIndex: action.payload.sectionIndex,
                rowIndex: action.payload.rowIndex,
            });
        },

        moveRowDown: (
            state,
            action: PayloadAction<WithFile<{ sectionIndex: number; rowIndex: number }>>
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = rowOps.moveRowDown(current(entry.document), {
                sectionIndex: action.payload.sectionIndex,
                rowIndex: action.payload.rowIndex,
            });
        },

        rotateRowLeft: (
            state,
            action: PayloadAction<WithFile<{ sectionIndex: number; rowIndex: number }>>
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = rowOps.rotateRowLeft(current(entry.document), {
                sectionIndex: action.payload.sectionIndex,
                rowIndex: action.payload.rowIndex,
            });
        },

        rotateRowRight: (
            state,
            action: PayloadAction<WithFile<{ sectionIndex: number; rowIndex: number }>>
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = rowOps.rotateRowRight(current(entry.document), {
                sectionIndex: action.payload.sectionIndex,
                rowIndex: action.payload.rowIndex,
            });
        },

        addTrackInRow: (
            state,
            action: PayloadAction<
                WithFile<{ sectionIndex: number; rowIndex: number; count?: number }>
            >
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = rowOps.addAdditionalVectorElementInARow(
                current(entry.document),
                {
                    sectionIndex: action.payload.sectionIndex,
                    rowIndex: action.payload.rowIndex,
                    count: action.payload.count,
                }
            );
        },

        removeTrackInRow: (
            state,
            action: PayloadAction<
                WithFile<{ sectionIndex: number; rowIndex: number; count?: number }>
            >
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = rowOps.removeAdditionalVectorElementInARow(
                current(entry.document),
                {
                    sectionIndex: action.payload.sectionIndex,
                    rowIndex: action.payload.rowIndex,
                    count: action.payload.count,
                }
            );
        },

        // --- cell operations ------------------------------------------------
        updateValueAtCell: (
            state,
            action: PayloadAction<
                WithFile<{
                    sectionIndex: number;
                    rowIndex: number;
                    vectorIndex: number;
                    cellIndex: number;
                    subCellIndex: number;
                    value: string;
                }>
            >
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            const doc = current(entry.document);
            // Still throws on an out-of-range path (pre-existing behaviour).
            entry.document = cellOps.updateValueAtCell(doc, {
                ...action.payload,
                vectorLength: doc.fileOptions.rows.vectorLength,
            });
        },

        insertSubCell: (
            state,
            action: PayloadAction<
                WithFile<{
                    sectionIndex: number;
                    rowIndex: number;
                    vectorIndex: number;
                }>
            >
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = cellOps.insertSubCell(current(entry.document), {
                sectionIndex: action.payload.sectionIndex,
                rowIndex: action.payload.rowIndex,
                vectorIndex: action.payload.vectorIndex,
            });
        },

        removeSubCell: (
            state,
            action: PayloadAction<
                WithFile<{
                    sectionIndex: number;
                    rowIndex: number;
                    vectorIndex: number;
                    subCellIndex: number;
                }>
            >
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = cellOps.removeSubCell(current(entry.document), {
                sectionIndex: action.payload.sectionIndex,
                rowIndex: action.payload.rowIndex,
                vectorIndex: action.payload.vectorIndex,
                subCellIndex: action.payload.subCellIndex,
            });
        },

        rotateSubCells: (
            state,
            action: PayloadAction<
                WithFile<{
                    sectionIndex: number;
                    rowIndex: number;
                    vectorIndex: number;
                    direction: "left" | "right";
                }>
            >
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = cellOps.rotateSubCells(current(entry.document), {
                sectionIndex: action.payload.sectionIndex,
                rowIndex: action.payload.rowIndex,
                vectorIndex: action.payload.vectorIndex,
                direction: action.payload.direction,
            });
        },

        // --- track operations -------------------------------------------------
        setSubRowName: (
            state,
            action: PayloadAction<
                WithFile<{
                    subRowIndex: number;
                    genericSubRow: boolean;
                    name: string;
                    sectionIndex: number;
                    rowIndex: number;
                }>
            >
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            // Throws on a duplicate name (pre-existing behaviour); the rename
            // dialog catches it.
            entry.document = trackOps.setSubRowName(current(entry.document), {
                subRowIndex: action.payload.subRowIndex,
                genericSubRow: action.payload.genericSubRow,
                name: action.payload.name,
                sectionIndex: action.payload.sectionIndex,
                rowIndex: action.payload.rowIndex,
            });
        },

        setSummaryTrackName: (
            state,
            action: PayloadAction<WithFile<{ trackName: string }>>
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = trackOps.setSummaryTrackName(
                current(entry.document),
                { trackName: action.payload.trackName }
            );
        },

        setTrackType: (
            state,
            action: PayloadAction<
                WithFile<{
                    sectionIndex: number;
                    rowIndex: number;
                    subRowIndex: number;
                    genericSubRow: boolean;
                    type: IRowRenderType;
                }>
            >
        ) => {
            const entry = state.files[action.payload.fileId];
            if (!entry) return;
            entry.document = trackOps.setTrackType(current(entry.document), {
                sectionIndex: action.payload.sectionIndex,
                rowIndex: action.payload.rowIndex,
                subRowIndex: action.payload.subRowIndex,
                genericSubRow: action.payload.genericSubRow,
                type: action.payload.type,
            });
        },
    },
});

export const notationActions = notationSlice.actions;
export default notationSlice.reducer;

// --- selectors ------------------------------------------------------------

export const selectFileEntry = (
    state: { notation: NotationState },
    fileId: string
): EditorFileEntry | undefined => state.notation.files[fileId];

export const selectDocument = (
    state: { notation: NotationState },
    fileId: string
): IReactStateFile | undefined => state.notation.files[fileId]?.document;

export const selectIsSavePending = (
    state: { notation: NotationState },
    fileId: string
): boolean => state.notation.files[fileId]?.savePending ?? false;

export const selectSaveStatus = (
    state: { notation: NotationState },
    fileId: string
): ISaveStatus | undefined => state.notation.files[fileId]?.saveStatus;
